import { fromJS, List } from 'immutable';
import { first, get, groupBy, sumBy } from 'lodash';
import * as actionTypes from './actionTypes';
import initialState from './initialState';
import { createReducer } from 'utils/redux';

const onReset = () => initialState;

// Get Draft data
const onGetDraftStart = (state) => state.set('loading', true).set('error', null);
const onGetDraftSuccess = (state, { payload }) =>
  state.set('loading', false).set('draft', fromJS(payload));
const onGetDraftError = (state, { payload }) =>
  state.set('loading', false).set('draft', null).set('error', fromJS(payload));

/**
 *
 * @param state
 * @param payload Map
 * @returns {*}
 */
const onSetDraft = (state, { payload }) => state.set('draft', payload);

// Update Draft data
const onUpdateDraftStart = (state) =>
  state.setIn(['draftSubmit', 'fetching'], true).setIn(['draftSubmit', 'error'], null);
const onUpdateDraftSuccess = (state, { payload }) =>
  state.setIn(['draftSubmit', 'fetching'], false).set('draft', fromJS(payload));
const onUpdateDraftError = (state, { payload }) =>
  state.setIn(['draftSubmit', 'fetching'], false).setIn(['draftSubmit', 'error'], fromJS(payload));

// Update DraftXML data
const onUpdateDraftXMLStart = (state) =>
  state.setIn(['draftXml', 'loading'], true).setIn(['draftXml', 'error'], null);
const onUpdateDraftXMLSuccess = (state, { payload }) =>
  state.setIn(['draftXml', 'loading'], false).setIn(['draftXml', 'data'], fromJS(payload));
const onUpdateDraftXMLError = (state, { payload }) =>
  state.setIn(['draftXml', 'loading'], false).setIn(['draftXml', 'error'], fromJS(payload));

// Initialize light deduction scenario (with common data)
const onInitialize = (state, { payload: { reportMode } }) => state.set('mode', reportMode);
const onInitializeSuccess = (state) => state.set('initialized', true);
const onInitializeError = (state, { payload }) =>
  state.set('initialized', false).set('error', fromJS(payload));

// Get parent declaration data
const onGetParentDeclaration = (state) =>
  state.setIn(['parentDeclaration', 'loading'], true).setIn(['parentDeclaration', 'error'], null);
const onGetParentDeclarationSuccess = (state, { payload }) =>
  state
    .setIn(['parentDeclaration', 'loading'], false)
    .setIn(['parentDeclaration', 'data'], fromJS(payload));
const onGetParentDeclarationError = (state, { payload }) =>
  state
    .setIn(['parentDeclaration', 'loading'], false)
    .setIn(['parentDeclaration', 'data'], null)
    .setIn(['parentDeclaration', 'error'], fromJS(payload));

// Get ndfl2 data by year
const onGetNDFL2 = (state) =>
  state.setIn(['ndfl2', 'loading'], true).setIn(['ndfl2', 'error'], null);
const onGetNDFL2Success = (state, { payload }) => {
  const ndfl2Grouped = groupBy(payload, 'ndfl2Id');
  const processedData = Object.keys(ndfl2Grouped).map((ndfl2Id) => ({
    id: ndfl2Id,
    companyName: get(first(ndfl2Grouped[ndfl2Id]), 'companyName'),
    incomeTotal: sumBy(ndfl2Grouped[ndfl2Id], 'incomeTotal'),
    taxesTotal: sumBy(ndfl2Grouped[ndfl2Id], 'taxesTotal'),
  }));
  return state
    .setIn(['ndfl2', 'loading'], false)
    .setIn(['ndfl2', 'data'], fromJS(processedData))
    .setIn(['ndfl2', 'incomeTotal'], sumBy(processedData, 'incomeTotal'))
    .setIn(['ndfl2', 'taxesTotal'], sumBy(processedData, 'taxesTotal'));
};
const onGetNDFL2Error = (state, { payload }) =>
  state
    .setIn(['ndfl2', 'loading'], false)
    .setIn(['ndfl2', 'data'], List())
    .setIn(['ndfl2', 'error'], fromJS(payload));

const onSetDeclarationSubmitted = (state) => state.set('declarationSubmitted', true);
const onResetDeclarationSubmitted = (state) => state.set('declarationSubmitted', false);

const onDraftError = (state, { payload }) => state.set('error', fromJS(payload));

// Set saved draft for the current mode
const onGetSavedDraftSuccess = (state, { payload = null }) =>
  state.set('savedDraft', fromJS(payload));
const resetSavedDraft = (state) => state.set('savedDraft', null);

const onStepFetchingStart = (state) => state.set('stepFetching', true);
const onStepFetchingStop = (state) => state.set('stepFetching', false);

export default createReducer(initialState, {
  [actionTypes.RESET_STATE]: onReset,

  [actionTypes.INITIALIZE]: onInitialize,
  [actionTypes.INITIALIZE_SUCCESS]: onInitializeSuccess,
  [actionTypes.INITIALIZE_FAILURE]: onInitializeError,

  [actionTypes.SET_DRAFT]: onSetDraft,
  [actionTypes.CREATE_DRAFT_FAILURE]: onDraftError,
  [actionTypes.REFINE_DRAFT_FAILURE]: onDraftError,

  [actionTypes.GET_DRAFT]: onGetDraftStart,
  [actionTypes.GET_DRAFT_SUCCESS]: onGetDraftSuccess,
  [actionTypes.GET_DRAFT_FAILURE]: onGetDraftError,

  [actionTypes.UPDATE_DRAFT]: onUpdateDraftStart,
  [actionTypes.UPDATE_DRAFT_SUCCESS]: onUpdateDraftSuccess,
  [actionTypes.UPDATE_DRAFT_ERROR]: onUpdateDraftError,

  [actionTypes.GET_PARENT_DECLARATION_BY_YEAR]: onGetParentDeclaration,
  [actionTypes.GET_PARENT_DECLARATION_BY_YEAR_SUCCESS]: onGetParentDeclarationSuccess,
  [actionTypes.GET_PARENT_DECLARATION_BY_YEAR_ERROR]: onGetParentDeclarationError,

  [actionTypes.GET_NDFL2_BY_YEAR]: onGetNDFL2,
  [actionTypes.GET_NDFL2_BY_YEAR_SUCCESS]: onGetNDFL2Success,
  [actionTypes.GET_NDFL2_BY_YEAR_ERROR]: onGetNDFL2Error,

  [actionTypes.UPDATE_XML_DRAFT]: onUpdateDraftXMLStart,
  [actionTypes.UPDATE_XML_DRAFT_SUCCESS]: onUpdateDraftXMLSuccess,
  [actionTypes.UPDATE_XML_DRAFT_FAILURE]: onUpdateDraftXMLError,

  [actionTypes.SET_DECLARATION_SUBMITTED]: onSetDeclarationSubmitted,
  [actionTypes.RESET_DECLARATION_SUBMITTED]: onResetDeclarationSubmitted,

  [actionTypes.GET_SAVED_DRAFTS_SUCCESS]: onGetSavedDraftSuccess,
  [actionTypes.GET_SAVED_DRAFTS_FAILURE]: resetSavedDraft,

  [actionTypes.RESET_SAVED_DRAFT]: resetSavedDraft,

  [actionTypes.START_STEP_FETCHING]: onStepFetchingStart,
  [actionTypes.STOP_STEP_FETCHING]: onStepFetchingStop,
});
